import { createSlice } from '@reduxjs/toolkit';
import {
  changePasswordBuilder,
  createMassPayUserBuilder,
  deleteAlbumMediaBuilder,
  deleteBundleBuilder,
  deleteWithdrawalMethodBuilder,
  editAlbumMediaBuilder,
  editBundleBuilder,
  editManualChatTotalSpentThresholdBuilder,
  fetchAlbumMediaBuilder,
  fetchAssetsBuilder,
  fetchBundleByIdBuilder,
  fetchInfluencersBuilder,
  fetchMediaBundlesBuilder,
  fetchReferralsBuilder,
  fetchUserEarningsBuilder,
  fetchUserInfoBuilder,
  fetchUserMonthlyEarningsBuilder,
  getOrCreateIdVerificationTokenBuilder,
  handleWithdrawalMethodsBuilder,
  loginBuilder,
  requestWithdrawalBuilder,
  testVoiceBuilder,
  updateAgencyBasicInfoBuilder,
  updateBasicInfoBuilder,
  updateOnboardingTypeBuilder,
  updateTelegramInfoBuilder,
  updateUserBasicInfoBuilder,
  uploadAssistedPhotoBuilder,
  uploadAssistedVideoBuilder,
  uploadBundleBuilder,
  uploadMediaBuilder,
  uploadMediaFromDropboxBuilder,
  uploadW9FormBuilder,
  verifyEmailBuilder,
} from './builder';
import { EARNINGS_TYPE, LOGOUT, UPLOAD_FILE_CONSTRAINTS } from '../../../utils/constants';
import { endOfDay, endOfMonth, startOfMonth, subMonths } from 'date-fns';
import { dateToUTC, isFirstDayOfMonthBeforeNoonUTC } from '../../../utils/date';

const INIT_STATE = {
  user: null,
  fetchInfluencersInfo: {
    pending: false,
    data: [],
    total: 0,
  },
  fetchAlbumMediaInfo: {
    data: [],
    counts: null,
    maxReached: false,
    pending: false,
    fetchMorePending: false,
  },
  fetchAssetsInfo: {
    data: [],
    maxReached: false,
    pending: false,
    fetchMorePending: false,
  },
  fetchMediaBundlesInfo: {
    data: [],
    pending: false,
  },
  monthlyInfluencerEarnings: null,
  pending: {
    fetchUserInfo: false,
    changePassword: false,
    fetchUserEarnings: false,
    handleWithdrawalMethods: false,
    updateUserBasicInfo: false,
    fetchUserMonthlyEarnings: false,
    editAlbumMedia: false,
    uploadMedia: false,
    deleteAlbumMedia: false,
    uploadBundle: false,
    fetchBundleById: false,
    deleteBundle: false,
    editBundle: false,
    fetchReferrals: false,
    uploadW9Form: false,
    testVoice: false,
    editManualChatTotalSpentThreshold: false,
    uploadMediaFromDropbox: false,
    getOrCreateIdVerificationToken: false,
  },
  dateRange: isFirstDayOfMonthBeforeNoonUTC()
    ? [
        dateToUTC(startOfMonth(subMonths(new Date(), 1))).toISOString(),
        dateToUTC(endOfMonth(subMonths(new Date(), 1))).toISOString(),
      ]
    : [dateToUTC(startOfMonth(new Date())).toISOString(), dateToUTC(endOfDay(new Date())).toISOString()],
  earningType: { value: EARNINGS_TYPE.GROSS, label: 'Gross' },
};

export const userSlice = createSlice({
  name: 'user',
  initialState: INIT_STATE,
  reducers: {
    updateNumOfUpdatedMedia: (state, { payload }) => {
      const { numOfMedia, type, paid } = payload;
      if (type === UPLOAD_FILE_CONSTRAINTS.TYPE.PHOTO) {
        if (paid) {
          state.user.numOfPremiumPhotos += numOfMedia;
        } else {
          state.user.numOfFreePhotos += numOfMedia;
        }
      } else if (type === UPLOAD_FILE_CONSTRAINTS.TYPE.VIDEO) {
        if (paid) {
          state.user.numOfPremiumVideos += numOfMedia;
        } else {
          state.user.numOfFreeVideos += numOfMedia;
        }
      }
    },
    setUserStatus: (state, { payload }) => {
      state.user.status = payload;
    },
    setDateRange: (state, { payload }) => {
      state.dateRange = payload;
    },
    setEarningType: (state, { payload }) => {
      state.earningType = payload;
    },
  },
  extraReducers: builder => {
    fetchUserInfoBuilder(builder);
    loginBuilder(builder);
    changePasswordBuilder(builder);
    verifyEmailBuilder(builder);
    updateBasicInfoBuilder(builder);
    fetchUserEarningsBuilder(builder);
    handleWithdrawalMethodsBuilder(builder);
    updateUserBasicInfoBuilder(builder);
    requestWithdrawalBuilder(builder);
    fetchInfluencersBuilder(builder);
    updateAgencyBasicInfoBuilder(builder);
    fetchUserMonthlyEarningsBuilder(builder);
    updateTelegramInfoBuilder(builder);
    fetchAlbumMediaBuilder(builder);
    fetchMediaBundlesBuilder(builder);
    editAlbumMediaBuilder(builder);
    uploadMediaBuilder(builder);
    deleteAlbumMediaBuilder(builder);
    uploadBundleBuilder(builder);
    fetchBundleByIdBuilder(builder);
    deleteBundleBuilder(builder);
    editBundleBuilder(builder);
    updateOnboardingTypeBuilder(builder);
    fetchReferralsBuilder(builder);
    deleteWithdrawalMethodBuilder(builder);
    uploadW9FormBuilder(builder);
    uploadAssistedPhotoBuilder(builder);
    uploadAssistedVideoBuilder(builder);
    fetchAssetsBuilder(builder);
    testVoiceBuilder(builder);
    createMassPayUserBuilder(builder);
    editManualChatTotalSpentThresholdBuilder(builder);
    uploadMediaFromDropboxBuilder(builder);
    getOrCreateIdVerificationTokenBuilder(builder);
    builder.addCase(LOGOUT, () => ({ ...INIT_STATE }));
  },
});

export const { updateNumOfUpdatedMedia, setUserStatus, setDateRange, setEarningType } = userSlice.actions;

export const selectUser = state => state.userInfo.user;
export const selectFetchInfluencersInfo = state => state.userInfo.fetchInfluencersInfo;
export const selectMonthlyInfluencerEarnings = state => state.userInfo.monthlyInfluencerEarnings;
export const selectUserActionsPending = state => state.userInfo.pending;
export const selectDateRange = state => state.userInfo.dateRange;
export const selectEarningType = state => state.userInfo.earningType;
export const selectFetchAlbumMediaInfo = state => state.userInfo.fetchAlbumMediaInfo;
export const selectFetchMediaBundlesInfo = state => state.userInfo.fetchMediaBundlesInfo;
export const selectFetchAssetsInfo = state => state.userInfo.fetchAssetsInfo;

export default userSlice.reducer;
